<template>
  <div class="batch-move-results">
    <div class="batch-move-results-header">
      <div
        v-if="batch"
        class="batch-move-results-destination"
      >
        <span class="batch-move-results-destination-label">Lote destino:</span>
        <span class="mnemonic-badge">{{ mnemonicLabel }}</span>
        <span
          v-if="batch.urgent"
          class="urgent-badge"
        >
          <svg-loader src="/static/images/icon-urgent-fast.svg" />
        </span>
        <span class="batch-move-results-destination-identifier">{{ batch.batchIdentifier }}</span>
      </div>

      <div
        v-if="summary"
        class="batch-move-results-summary"
      >
        <span>Total: <b>{{ summary.total }}</b></span>
        <span class="success-count">Sucesso: <b>{{ summary.success }}</b></span>
        <span
          v-if="summary.failed > 0"
          class="failed-count"
        >Falhas: <b>{{ summary.failed }}</b></span>
        <span
          v-if="summary.outboxQueued > 0"
          class="queued-count"
        >Pendentes: <b>{{ summary.outboxQueued }}</b></span>
      </div>
    </div>

    <div class="batch-move-results-table-wrapper">
      <table class="batch-move-results-table">
        <thead>
          <tr>
            <th>Código de barras</th>
            <th>Status</th>
            <th>Detalhe</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="item in results"
            :key="item.barcode"
            :class="rowClass(item.status)"
          >
            <td>{{ item.barcode }}</td>
            <td>
              <span :class="['status-badge', badgeClass(item.status)]">
                {{ statusLabel(item.status) }}
              </span>
            </td>
            <td>{{ detailFor(item) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <footer class="batch-move-results-footer">
      <md-button
        v-if="showCloseButton"
        class="btn btn-nonenary"
        @click="handleClose"
      >
        {{ closeLabel }}
      </md-button>

      <md-button
        v-if="canOpenBatch"
        class="btn btn-secondary"
        @click="handleOpenBatch"
      >
        Abrir lote
      </md-button>

      <md-button
        v-if="hasFailures"
        class="btn btn-primary"
        :disabled="isRetrying"
        @click="handleRetry"
      >
        Tentar novamente os que falharam
      </md-button>
    </footer>
  </div>
</template>

<script>
import { translateBatchErrorMessage } from '@/utils/batchErrorCodes'

export default {
  name: 'BatchMoveResultsTable',
  props: {
    results: {
      type: Array,
      required: true,
      default: () => [],
    },
    summary: {
      type: Object,
      default: null,
    },
    isRetrying: {
      type: Boolean,
      default: false,
    },
    closeLabel: {
      type: String,
      default: 'Fechar',
    },
    batch: {
      type: Object,
      default: null,
    },
    targetBatchIdentifier: {
      type: String,
      default: null,
    },
  },
  computed: {
    hasFailures() {
      return (this.results || []).some(r => r.status !== 'OK')
    },
    hasSuccesses() {
      return (this.results || []).some(r => r.status === 'OK')
    },
    resolvedTargetIdentifier() {
      if (this.targetBatchIdentifier) return this.targetBatchIdentifier
      if (this.summary && this.summary.targetBatchIdentifier) return this.summary.targetBatchIdentifier
      if (this.batch && this.batch.batchIdentifier) return this.batch.batchIdentifier
      return null
    },
    canOpenBatch() {
      return this.hasSuccesses && Boolean(this.resolvedTargetIdentifier)
    },
    otherActionsCount() {
      let count = 0
      if (this.canOpenBatch) count += 1
      if (this.hasFailures) count += 1
      return count
    },
    showCloseButton() {
      return this.otherActionsCount <= 1
    },
    mnemonicLabel() {
      if (!this.batch || !this.batch.label) return ''
      if (this.batch.sequence == null) return this.batch.label
      return `${this.batch.label}.${this.batch.sequence}`
    },
  },
  methods: {
    statusLabel(status) {
      if (status === 'OK') return 'Sucesso'
      if (status === 'OUTBOX_QUEUED') return 'Pendente'
      return 'Falha'
    },
    badgeClass(status) {
      if (status === 'OK') return 'status-badge--success'
      if (status === 'OUTBOX_QUEUED') return 'status-badge--queued'
      return 'status-badge--failed'
    },
    rowClass(status) {
      if (status === 'OK') return 'row-success'
      if (status === 'OUTBOX_QUEUED') return 'row-queued'
      return 'row-failed'
    },
    detailFor(item) {
      if (item.status === 'OK') {
        return (item.info && item.info.message) || 'Movida com sucesso.'
      }
      if (item.status === 'OUTBOX_QUEUED') {
        return (
          translateBatchErrorMessage(item) ||
          (item.info && item.info.message) ||
          'Sincronização pendente. Será concluída automaticamente.'
        )
      }
      return (
        translateBatchErrorMessage(item) ||
        (item && item.errorMessage) ||
        'Falha ao mover amostra.'
      )
    },
    handleClose() {
      this.$emit('close')
    },
    handleRetry() {
      this.$emit('retry-failed')
    },
    handleOpenBatch() {
      this.$emit('open-batch', this.resolvedTargetIdentifier)
    },
  },
}
</script>

<style lang="scss" scoped>
.batch-move-results {
  font-family: 'Exo 2', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 40rem;

  &-header {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &-destination {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: #818181;

    &-label {
      font-weight: 700;
      text-transform: uppercase;
    }

    &-identifier {
      font-weight: 500;
      margin-left: 0.25rem;
    }
  }

  &-summary {
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;
    color: #666666;

    .success-count {
      color: #2e7d32;
    }

    .failed-count {
      color: #c62828;
    }

    .queued-count {
      color: #ef6c00;
    }
  }

  &-table-wrapper {
    max-height: 18rem;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 0.25rem;
  }

  &-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;

    thead {
      background-color: #f5f5f5;
      position: sticky;
      top: 0;
    }

    th,
    td {
      padding: 0.5rem 0.75rem;
      text-align: left;
      border-bottom: 1px solid #eeeeee;
    }

    th {
      font-weight: 600;
      color: #333333;
    }

    .row-success td {
      color: #1b1b1b;
    }

    .row-failed td {
      color: #c62828;
    }

    .row-queued td {
      color: #ef6c00;
    }
  }

  .status-badge {
    display: inline-block;
    padding: 0.125rem 0.5rem;
    border-radius: 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;

    &--success {
      background-color: #e8f5e9;
      color: #2e7d32;
    }

    &--failed {
      background-color: #ffebee;
      color: #c62828;
    }

    &--queued {
      background-color: #fff3e0;
      color: #ef6c00;
    }
  }

  &-footer {
    display: flex;
    gap: 1rem;

    .md-button {
      flex: 1;
    }
  }

  .mnemonic-badge {
    background-color: #4a4a4a;
    border-radius: 2px;
    color: white;
    padding: 1px 6px;
    font-family: 'Exo 2';
    font-weight: 600;
    font-size: 12px;
    opacity: 0.93;
    text-transform: uppercase;
  }

  .urgent-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 4px;
    height: 20px;
    color: white;
    background: #ffbf00;
    border-radius: 4px 12px 12px 4px;

    svg {
      height: 16px;
      width: 16px;
    }
  }
}
</style>
